// THE PRO SELF-HEAL — the missing half of auth.ts's operator self-heal.
//
// The Hub resolves a pro by pros.auth_user_id ONLY. Operators got an email
// self-heal (auth.ts:121) the day the portal shipped; pros never did, so a pro
// whose row was seeded from Zoho signed in with a verified magic link and saw
// an EMPTY hub — measured 2026-08-26: 14 of Sharon's 18 active pros and all 14
// of Ogden's were unlinked. The operator flow can live client-side because
// operator_users RLS happens to permit it; pros RLS cannot let an unlinked
// user touch a row by email, so the write is the SECURITY DEFINER RPC
// public.pro_link_self() (backend migration 20260830000000). It links only
// rows carrying the CALLER'S OWN JWT-verified email, and only where
// auth_user_id IS NULL — it can never re-point an identity.
//
// Runs ONCE per browser session, gated by App.tsx BEFORE the first page
// renders — pages query pros the moment they mount, and a heal racing those
// queries would show the empty hub this exists to prevent. FAIL-OPEN by
// design: an operator with no pros row, a network blip, or the RPC not yet
// deployed must never lock anyone out of the app.
import { supabase } from '@/lib/supabase';
import { isCell } from './cell/config';
import { cellGet, cellPost } from './cell/api';

const FLAG = (userId: string) => `pro-heal-done:${userId}`;

export async function ensureProLinked(userId: string): Promise<void> {
  try {
    if (sessionStorage.getItem(FLAG(userId))) return;

    // Already linked? Most sessions end here with one cheap indexed read.
    // CELL (M28): GET /v1/pro-hub/users/{authUserId}/pro answers the same count (linked).
    const cellMe = isCell() ? await cellGet<{ linked: number }>(`/v1/pro-hub/users/${userId}/pro`) : null;
    const { count, error } = cellMe ? { count: cellMe.data?.linked ?? null, error: cellMe.error } : await supabase
      .from('pros')
      .select('id', { count: 'exact', head: true })
      .eq('auth_user_id', userId);
    if (!error && (count ?? 0) > 0) {
      sessionStorage.setItem(FLAG(userId), '1');
      return;
    }

    // Unlinked (or the count failed): ask the database to link the caller's
    // own row. Returns the pro id when a row matched, null when none did
    // (an operator signing in has no pros row — that is not an error).
    // CELL (M28): POST /v1/pro-hub/link-self (pro_link_self as the caller).
    const { data, error: rpcError } = isCell()
      ? await cellPost<{ proId: string | null }>('/v1/pro-hub/link-self').then((r) => ({ data: r.data?.proId ?? null, error: r.error }))
      : await supabase.rpc('pro_link_self');
    if (!rpcError && data) sessionStorage.setItem(FLAG(userId), '1');
    // No flag on null/error: a pros row seeded LATER for this person gets
    // linked on their next session instead of never.
  } catch (e) {
    // supabase-js can reject with a plain object, not an Error — log the
    // shape we actually got rather than "[object Object]".
    console.warn('pro self-heal skipped:', JSON.stringify(e));
  }
}
